import { useMemo } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type {
  Lodging,
  TripAdvisorReviews,
} from "@b2bportal/lodging-api/lib/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, TripadvisorRatingIcon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./TripAdvisorRating.module.scss";

export type TripAdvisorRatingProps = PlatformComponentProps & {
  tripAdvisorReviews: TripAdvisorReviews;
  isMinimal?: boolean;
  showReviewCount?: boolean;
};

export const TripAdvisorRating = ({
  tripAdvisorReviews,
  className,
  isMinimal = false,
  showReviewCount = false,
}: TripAdvisorRatingProps) => {
  const styles = useHotelsStyles(
    CoreHotelsComponent.TripAdvisorRating,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreHotelsComponent.TripAdvisorRating
  );

  const { t } = useI18nContext();

  const scoreDecimal = tripAdvisorReviews?.overallScore * 10;

  const label = useMemo(() => {
    if (scoreDecimal >= 9) {
      return t("hotels.rating.excellent");
    } else if (scoreDecimal >= 8) {
      return t("hotels.rating.veryGood");
    } else if (scoreDecimal >= 6) {
      return t("hotels.rating.good");
    } else if (scoreDecimal >= 4) {
      return t("hotels.rating.average");
    } else {
      return t("hotels.rating.poor");
    }
  }, [scoreDecimal, t]);

  const scoreLabel = useMemo(() => {
    return scoreDecimal.toFixed(1);
  }, [scoreDecimal]);

  if (tripAdvisorReviews == null || tripAdvisorReviews.overallScore == null) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      <div className={cn("left-container")}>
        <div className={cn("label-container", { hidden: isMinimal })}>
          {label}
        </div>
        <div className={cn("tripadvisor-icon-container")}>
          <Icon
            useCurrentColor={false}
            className={cn("tripadvisor-icon")}
            iconName={IconNameEnum.tripadvisor}
          />

          <TripadvisorRatingIcon
            scoreOutOfFive={
              Math.round(tripAdvisorReviews.overallScore * 10) / 2
            }
            className={cn("tripadvisor-icon-image")}
          />
          {showReviewCount && (
            <div className={cn("review-count")}>
              {tripAdvisorReviews.reviewsCount}
            </div>
          )}
        </div>
      </div>
      <div className={cn("score-container", { hidden: isMinimal })}>
        {scoreLabel}
      </div>
    </div>
  );
};
